import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Card,
  Badge,
  Alert,
  Progress,
  Box,
  Grid,
  GridCol
} from '@mantine/core';
import { YearPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconRefresh,
  IconCheck,
  IconDatabase,
  IconCalendar,
  IconAlertTriangle,
  IconInfoCircle
} from '@tabler/icons-react';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import firestore from '../../utils/firestore';
import { getFestivalForDate } from '../../utils/festivalHelper';
import calendar from '../../utils/lunar';

const FestivalManager = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalRecords: 0,
    recordsWithFestival: 0,
    recordsWithoutFestival: 0,
    uniqueFestivals: new Set(),
    festivalDetails: []
  });
  const [selectedYear, setSelectedYear] = useState(null);
  const [backfillResults, setBackfillResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const q = query(collection(firestore, 'accounting'));
      const snapshot = await getDocs(q);
      
      let recordsWithFestival = 0;
      let recordsWithoutFestival = 0;
      const uniqueFestivals = new Set();
      
      const festivalMap = new Map();
      
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.lunar && data.lunar !== '') {
          recordsWithFestival++;
          uniqueFestivals.add(data.lunar);
          
          const todayLunar = calendar.solar2lunar(data.year, data.month + 1, data.day);
          
          if (!festivalMap.has(data.lunar)) {
            festivalMap.set(data.lunar, {
              name: data.lunar,
              lunarMonth: todayLunar.lMonth,
              lunarDay: todayLunar.lDay,
              sortKey: `${String(todayLunar.lMonth).padStart(2, '0')}-${String(todayLunar.lDay).padStart(2, '0')}`
            });
          }
        } else {
          recordsWithoutFestival++;
        }
      });
      
      const festivalDetails = Array.from(festivalMap.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
      
      setStats({
        totalRecords: snapshot.size,
        recordsWithFestival,
        recordsWithoutFestival,
        uniqueFestivals,
        festivalDetails
      });
    } catch (error) {
      console.error('載入統計資料失敗:', error);
      notifications.show({
        title: '載入失敗',
        message: '無法載入統計資料',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackfill = async (dryRun = true) => {
    setLoading(true);
    setBackfillResults(null);
    setLogs([]);
    setProgress(0);
    
    try {
      const addLog = (message) => {
        setLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message }]);
      };
      
      addLog('🚀 開始補充節日資料...');
      addLog(`📋 模式: ${dryRun ? '試運行（不實際更新）' : '正式執行'}`);
      
      let q;
      if (selectedYear) {
        q = query(
          collection(firestore, 'accounting'),
          where('year', '==', selectedYear)
        );
      } else {
        q = query(collection(firestore, 'accounting'));
      }
      
      const snapshot = await getDocs(q);
      addLog(`📊 找到 ${snapshot.size} 筆記錄`);
      
      const recordsToUpdate = [];
      const recordsToSkip = [];
      
      let processed = 0;
      const total = snapshot.size;
      
      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        const { id } = docSnapshot;
        
        if (!data.lunar || data.lunar === '') {
          const festivalName = getFestivalForDate(data.year, data.month + 1, data.day);
          
          if (festivalName && festivalName !== '') {
            recordsToUpdate.push({
              id,
              data: {
                ...data,
                lunar: festivalName
              }
            });
          } else {
            recordsToSkip.push({
              id,
              date: `${data.year}/${data.month + 1}/${data.day}`,
              reason: '該日期無節日'
            });
          }
        } else {
          recordsToSkip.push({
            id,
            date: `${data.year}/${data.month + 1}/${data.day}`,
            reason: '已有節日資料'
          });
        }
        
        processed++;
        setProgress(Math.round((processed / total) * 100));
      }
      
      addLog(`📋 分析結果:`);
      addLog(`  - 需要更新: ${recordsToUpdate.length} 筆`);
      addLog(`  - 跳過: ${recordsToSkip.length} 筆`);
      
      if (recordsToUpdate.length > 0) {
        addLog(`\n📝 預計更新明細 (前10筆):`);
        recordsToUpdate.slice(0, 10).forEach(record => {
          addLog(`  - ${record.data.year}/${record.data.month + 1}/${record.data.day}: ${record.data.lunar}`);
        });
        
        if (recordsToUpdate.length > 10) {
          addLog(`  ... 還有 ${recordsToUpdate.length - 10} 筆`);
        }
      }
      
      if (!dryRun && recordsToUpdate.length > 0) {
        addLog(`\n💾 開始更新...`);
        
        const batch = writeBatch(firestore);
        
        for (const record of recordsToUpdate) {
          const docRef = doc(firestore, 'accounting', record.id);
          batch.update(docRef, { lunar: record.data.lunar });
        }
        
        await batch.commit();
        addLog(`✅ 更新完成！`);
      }
      
      setBackfillResults({
        totalFound: snapshot.size,
        totalUpdated: recordsToUpdate.length,
        totalSkipped: recordsToSkip.length,
        dryRun
      });
      
      addLog(`\n🎉 ${dryRun ? '試運行' : '補充'}完成！`);
      
      notifications.show({
        title: dryRun ? '試運行完成' : '補充完成',
        message: dryRun 
          ? `預計將更新 ${recordsToUpdate.length} 筆記錄（總共 ${snapshot.size} 筆已處理）`
          : `成功更新 ${recordsToUpdate.length} 筆記錄（總共 ${snapshot.size} 筆已處理）`,
        color: dryRun ? 'blue' : 'green',
        autoClose: dryRun ? 8000 : 5000
      });
      
      if (!dryRun) {
        loadStats();
      }
      
    } catch (error) {
      console.error('補充失敗:', error);
      notifications.show({
        title: '補充失敗',
        message: error.message,
        color: 'red'
      });
      setLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `❌ 錯誤: ${error.message}` }]);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Title order={1}>節日資料管理</Title>
        
        <Alert icon={<IconInfoCircle size="1rem" />} color="blue">
          <Text>
            此頁面用於管理和補充節日資料。節日資料會根據農曆日期自動識別並填充到記帳記錄中。
          </Text>
        </Alert>
        
        <Grid>
          <GridCol span={4}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Text size="sm" c="dimmed">總記錄數</Text>
                <IconDatabase size="1.2rem" color="#228be6" />
              </Group>
              <Title order={2}>{stats.totalRecords}</Title>
            </Card>
          </GridCol>
          
          <GridCol span={4}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Text size="sm" c="dimmed">已有節日</Text>
                <IconCalendar size="1.2rem" color="#40c057" />
              </Group>
              <Title order={2} c="green">{stats.recordsWithFestival}</Title>
            </Card>
          </GridCol>
          
          <GridCol span={4}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Text size="sm" c="dimmed">缺少節日</Text>
                <IconAlertTriangle size="1.2rem" color="#fa5252" />
              </Group>
              <Title order={2} c="red">{stats.recordsWithoutFestival}</Title>
            </Card>
          </GridCol>
        </Grid>
        
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">批量補充節日</Title>
          
          {backfillResults && backfillResults.dryRun && (
            <Alert icon={<IconInfoCircle size="1rem" />} color="blue" mb="md">
              <Stack gap="xs">
                <Text fw={500} size="lg">📊 試運行結果摘要</Text>
                <Text size="sm">- 總共處理: {backfillResults.totalFound} 筆記錄</Text>
                <Text size="sm" fw={500} c="blue">⚠️ 預計將修改 {backfillResults.totalUpdated} 筆記錄</Text>
                <Text size="sm">- 跳過: {backfillResults.totalSkipped} 筆</Text>
                {backfillResults.totalUpdated === 0 && (
                  <Text size="sm" c="green">✅ 所有記錄都已有節日資料，無需更新</Text>
                )}
              </Stack>
            </Alert>
          )}
          
          <Stack gap="md">
            <YearPickerInput
              label="選擇年份（可選）"
              placeholder="選擇年份"
              value={selectedYear}
              onChange={setSelectedYear}
              clearable
            />
            
            <Group>
              <Button
                onClick={() => handleBackfill(true)}
                disabled={loading}
                variant="light"
                leftSection={<IconRefresh size="1rem" />}
              >
                試運行（不實際更新）
              </Button>
              
              <Button
                onClick={() => handleBackfill(false)}
                disabled={loading}
                color="green"
                leftSection={<IconCheck size="1rem" />}
              >
                正式執行補充
              </Button>
              
              <Button
                onClick={loadStats}
                disabled={loading}
                variant="outline"
                leftSection={<IconDatabase size="1rem" />}
              >
                重新載入統計
              </Button>
            </Group>
            
            {loading && (
              <>
                <Progress value={progress} />
                <Text size="sm" c="dimmed">處理中...</Text>
              </>
            )}
            
            {backfillResults && (
              <Alert 
                icon={backfillResults.dryRun ? <IconRefresh size="1rem" /> : <IconCheck size="1rem" />} 
                color={backfillResults.dryRun ? "blue" : "green"}
                title={backfillResults.dryRun ? "試運行結果（預覽）" : "執行結果"}
              >
                <Stack gap="xs">
                  {backfillResults.dryRun ? (
                    <Text>📋 試運行完成！這只是預覽，資料庫未被修改。</Text>
                  ) : (
                    <Text>✅ 處理完成！資料庫已更新。</Text>
                  )}
                  <Text size="sm">- 總共處理: {backfillResults.totalFound} 筆記錄</Text>
                  <Text size="sm" fw={500} c={backfillResults.dryRun ? "blue" : "green"}>
                    - {backfillResults.dryRun ? '預計更新' : '已成功更新'}: {backfillResults.totalUpdated} 筆
                  </Text>
                  <Text size="sm">- 跳過: {backfillResults.totalSkipped} 筆</Text>
                  {backfillResults.dryRun && backfillResults.totalUpdated > 0 && (
                    <Text size="sm" c="dimmed" mt="xs">
                      💡 提示：點擊「正式執行補充」可實際更新這 {backfillResults.totalUpdated} 筆記錄
                    </Text>
                  )}
                </Stack>
              </Alert>
            )}
            
            {logs.length > 0 && (
              <Box
                style={{
                  backgroundColor: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}
              >
                <Text size="sm" weight={500} mb="xs">執行日誌:</Text>
                {logs.map((log, index) => (
                  <Text key={index} size="xs" style={{ fontFamily: 'monospace' }}>
                    [{log.time}] {log.message}
                  </Text>
                ))}
              </Box>
            )}
          </Stack>
        </Card>
        
        {stats.festivalDetails && stats.festivalDetails.length > 0 && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">節日列表（按農曆日期排序）</Title>
            <Group gap="xs" wrap>
              {stats.festivalDetails.map((item, index) => (
                <Badge key={index} variant="light" color="blue" size="lg">
                  {item.name} ({item.lunarMonth}/{item.lunarDay})
                </Badge>
              ))}
            </Group>
          </Card>
        )}
      </Stack>
    </Container>
  );
};

export default FestivalManager;
